// ÚJ JEGY (belső nyitás) — a legacy `OpenTicket` Livewire-form utódja, ÚJRATERVEZETT felülettel.
//
// A LOGIKA 1:1 A LEGACY-VEL:
//   • fiók → szolgáltatás → igénylő SORREND: a fiók választása után a szerver adja a szolgáltatás-listát KÉT
//     csoportban (a fiók által használt / egyéb) és a fiók kapcsolattartóit (`ticketFormOptions`),
//   • egyetlen kapcsolattartó → AUTOMATIKUSAN kiválasztva (a legacy `filterContacts()` viselkedése),
//   • fiók-váltás → a szolgáltatás és az igénylő ürül (a legacy `updatedSelectedAccount()`),
//   • kötelező: fiók · szolgáltatás · igénylő · típus · tárgy · leírás (min. 25 LÁTHATÓ karakter),
//   • a CSAPAT és a FELELŐS NINCS a formon: azokat a szerver oldja fel a szolgáltatásból (a legacy is így
//     tette — a nyitó-formon nem is voltak mezők; a szerkesztőben viszont módosíthatók).
//
// A FELÜLET viszont szándékosan nem a legacy egy-hasábos mező-listája: HÁROM, számozott szakasz (a számozás
// VALÓDI sorrend — a fiók nélkül a szolgáltatás/igénylő nem is tölthető), a típus ikonos szegmens-választó,
// a tárgy/leírás élő számlálóval, a fájl-linkek pedig egy összecsukott blokkban (opcionális, ritkán használt).
import { TICKET_TYPES, type TicketType } from '@atlas/shared';
import {
  BanknotesIcon,
  BugAntIcon,
  EllipsisHorizontalCircleIcon,
  LifebuoyIcon,
  SparklesIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { DropboxMark, GoogleDriveMark, OneDriveMark } from '@/components/brand/service-marks';
import { type ComboboxOption } from '@/components/shared/entity-combobox';
import {
  ComboboxField,
  RichTextField,
  SelectField,
  TextField,
} from '@/components/shared/form-fields';
import { FormSheet } from '@/components/shared/form-sheet';
import { PendingAttachmentsField } from '@/components/shared/pending-attachments-field';
import { TicketTypeBadge } from '@/components/tickets/ticket-type-badge';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Label } from '@/components/ui/label';
import { ticketKeys } from '@/features/tickets/api';
import { MAX_ATTACHMENTS, uploadTicketAttachment } from '@/features/tickets/attachment-upload';
import { api } from '@/lib/api';
import { toPersonOptionsWith } from '@/lib/person-options';
import { richTextToPlainText } from '@/lib/sanitize-html';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

/**
 * A jegy-típusok ikonja (a szegmens-választóhoz). `Record` a közös enumon: egy új típusnál a fordító szól, ha
 * az ikon hiányzik. A sorrend a `TICKET_TYPES`-é (a legacy enum sorrendje).
 */
const TYPE_ICONS: Record<TicketType, typeof LifebuoyIcon> = {
  support: LifebuoyIcon,
  bug: BugAntIcon,
  feature_request: SparklesIcon,
  billing: BanknotesIcon,
  other: EllipsisHorizontalCircleIcon,
};
const TYPES = TICKET_TYPES.map((value) => ({ value, Icon: TYPE_ICONS[value] }));

/** A leírás minimuma LÁTHATÓ karakterben (a legacy `min:25`) — a számláló ehhez méri magát. */
const DESCRIPTION_MIN = 25;
/** A tárgy plafonja (a legacy `max:255`). */
const SUBJECT_MAX = 255;

// A leírás RICH-TEXT (HTML-string) — a hossz-validáció a NYERS szöveg hosszára fut (nem a HTML-tag-ekre), ezt a
// komponens-szintű, i18n-üzenetes séma adja (lásd lent). A `baseSchema` a mező-shape + a többi mező validációja.
const baseSchema = z.object({
  accountId: z.string().min(1),
  serviceId: z.string().min(1),
  requesterId: z.string().min(1),
  type: z.enum(TICKET_TYPES),
  subject: z.string().trim().min(1).max(SUBJECT_MAX),
  description: z.string(), // a valódi (plain-text min-25) ellenőrzés a komponens-szintű sémában, i18n-üzenettel
  // Online-tárhely-linkek (opcionálisak) — a legacy `nullable|url` mezői.
  googleDriveLink: z.string().trim().max(2083),
  microsoftOnedriveLink: z.string().trim().max(2083),
  dropboxLink: z.string().trim().max(2083),
});
type FormValues = z.infer<typeof baseSchema>;

const EMPTY: FormValues = {
  accountId: '',
  serviceId: '',
  requesterId: '',
  type: 'support',
  subject: '',
  description: '',
  googleDriveLink: '',
  microsoftOnedriveLink: '',
  dropboxLink: '',
};

export interface TicketCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function TicketCreateSheet({ open, onOpenChange }: TicketCreateSheetProps): JSX.Element {
  const { t, i18n } = useTranslation();

  // A leírás plain-text hossz-validációja (min. 25 látható karakter) i18n-üzenettel — ezért a komponensben épül.
  const formSchema = useMemo(
    () =>
      baseSchema.extend({
        // MEZŐNKÉNTI üzenetek (i18n) — üzenet nélkül a Zod ANGOL alapszövege jelent meg („String must contain
        // at least 1 character(s)"); a többi mezőt a globális fallback-térkép fordítja (`lib/zod-error-map.ts`).
        accountId: z.string().min(1, t('validation:tickets.account')),
        serviceId: z.string().min(1, t('validation:tickets.service')),
        requesterId: z.string().min(1, t('validation:tickets.requester')),
        subject: z
          .string()
          .trim()
          .min(1, t('validation:tickets.subject.required'))
          .max(SUBJECT_MAX, t('validation:tickets.subject.max')),
        googleDriveLink: z.string().trim().max(2083, t('validation:tickets.link.max')),
        microsoftOnedriveLink: z.string().trim().max(2083, t('validation:tickets.link.max')),
        dropboxLink: z.string().trim().max(2083, t('validation:tickets.link.max')),
        description: z
          .string()
          .max(20000)
          .refine((v) => richTextToPlainText(v).length >= DESCRIPTION_MIN, {
            message: t('validation:tickets.description.min'),
          }),
      }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema) as Resolver<FormValues>,
    mode: 'onTouched', // validáció már mentés előtt: a mezőből kilépve (blur), utána élőben
    defaultValues: EMPTY,
  });
  const { handleSubmit, reset, setError, setValue, watch } = form;

  const accountId = watch('accountId');
  const serviceId = watch('serviceId');
  const subject = watch('subject');
  const description = watch('description');

  // A FIÓK-FÜGGŐ opciók a szerverről (szolgáltatás-csoportok + a fiók kapcsolattartói). Fiók nélkül nem hívjuk.
  const optionsKey = ['ticket-form-options', accountId];
  const optionsQ = api.ticketFormOptions.useQuery(
    optionsKey,
    { query: { accountId } },
    { queryKey: optionsKey, enabled: accountId !== '' },
  );
  const options = optionsQ.data?.status === 200 ? optionsQ.data.body : undefined;
  const optionsLoading = accountId !== '' && optionsQ.isFetching && options === undefined;

  // FIÓK-VÁLTÁS → a fiók-függő mezők ürülnek (a legacy `updatedSelectedAccount()`): a korábbi szolgáltatás/
  // igénylő MÁS fiókhoz tartozott, tehát bent hagyva idegen adatot küldenénk. Csak VÁLTÁSKOR fut (nem mountra).
  const prevAccount = useRef(accountId);
  useEffect(() => {
    if (prevAccount.current === accountId) return;
    prevAccount.current = accountId;
    setValue('serviceId', '', { shouldValidate: false });
    setValue('requesterId', '', { shouldValidate: false });
  }, [accountId, setValue]);

  // EGYETLEN kapcsolattartó → automatikusan kiválasztva (a legacy `filterContacts()` viselkedése).
  const requesters = options?.requesters ?? [];
  useEffect(() => {
    if (requesters.length === 1 && watch('requesterId') === '') {
      setValue('requesterId', requesters[0]?.id ?? '', { shouldValidate: false });
    }
  }, [requesters, setValue, watch]);

  // Fiók-kereső (a lista-endpoint q-jával) → {id,label}.
  const searchAccounts = async (q: string): Promise<ComboboxOption[]> => {
    const r = await api.accounts.query({ query: { q: q || undefined, limit: 20 } });
    return r.status === 200 ? r.body.accounts.map((a) => ({ id: a.id, label: a.name })) : [];
  };

  const mutation = useEntityMutation<FormValues>({
    mutationFn: (v) =>
      api.ticketCreate.mutation({
        body: {
          accountId: v.accountId,
          serviceId: v.serviceId,
          requesterId: v.requesterId,
          type: v.type,
          subject: v.subject,
          description: v.description,
          googleDriveLink: v.googleDriveLink.trim() === '' ? null : v.googleDriveLink.trim(),
          microsoftOnedriveLink:
            v.microsoftOnedriveLink.trim() === '' ? null : v.microsoftOnedriveLink.trim(),
          dropboxLink: v.dropboxLink.trim() === '' ? null : v.dropboxLink.trim(),
        },
      }),
    invalidateKeys: [ticketKeys.lists()],
    onFieldErrors: (field, messages) => {
      if (field in EMPTY) setError(field as keyof FormValues, { message: messages.join(' ') });
    },
    messages: {
      success: t('toast:tickets.created'),
      conflict: t('toast:tickets.conflict'),
      forbidden: t('toast:tickets.forbidden'),
      validation: t('toast:tickets.validation'),
      error: t('toast:tickets.error'),
    },
  });

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: (res) => {
        // A JEGY MEGVAN → a csatolmányok utána mennek fel (a jegy id-jére). A form ezt megvárja: a felhasználó
        // lássa, ha egy fájl elhasalt (a jegy akkor is nyitva van — külön, néven nevezett hibaüzenet megy).
        // A hook `body`-ja szándékosan `unknown` (generikus mutation-eredmény) → szűkítjük.
        const body = res.body as { id?: unknown };
        const ticketId = typeof body.id === 'string' ? body.id : '';
        const pending = files;
        reset(EMPTY);
        setFiles([]);
        onOpenChange(false);
        if (ticketId === '' || pending.length === 0) return;
        void (async () => {
          const failed: string[] = [];
          for (const file of pending) {
            const outcome = await uploadTicketAttachment(ticketId, file);
            if (outcome !== 'ok') failed.push(file.name);
          }
          if (failed.length > 0) {
            toast.error(t('toast:tickets.attachmentPartial', { files: failed.join(', ') }));
          } else {
            toast.success(t('toast:tickets.attachmentUploaded'));
          }
        })();
      },
    });
  });

  // A kiválasztott szolgáltatás → MELYIK csapathoz kerül a jegy. Csak jelzés: a routing szerver-oldali.
  const selectedService = [
    ...(options?.services.existing ?? []),
    ...(options?.services.other ?? []),
  ].find((s) => s.id === serviceId);

  const serviceGroups = [
    {
      label: t('tickets.form.servicesOfAccount'),
      options: (options?.services.existing ?? []).map((s) => ({ value: s.id, label: s.name })),
    },
    {
      label: t('tickets.form.otherServices'),
      options: (options?.services.other ?? []).map((s) => ({ value: s.id, label: s.name })),
    },
  ];
  // Az igénylők (a fiók kapcsolattartói) nyelvhelyes névvel és az adott nyelv ABC-rendjében; az e-mail a
  // névazonos kapcsolattartók megkülönböztetéséhez marad a címkében.
  const requesterOptions = toPersonOptionsWith(requesters, i18n.language).map((r) => ({
    value: r.id,
    label: r.email !== null && r.email !== '' ? `${r.name} · ${r.email}` : r.name,
  }));

  const typeOptions = TYPES.map((x) => ({
    ...x,
    label: t(`tickets.typeLabel.${x.value}`, { defaultValue: x.value }),
  }));

  /**
   * A NYITÁSKOR csatolt fájlok. A legacy nyitó-formon is lehetett csatolmányt adni; az Atlas csatolmány-API-ja
   * viszont ticket-hez kötött (`/tickets/:id/attachments`), ezért a fájlok a jegy LÉTREJÖTTE UTÁN mennek fel.
   * Ez tudatos: a bájtok közvetlenül a tárba mennek, a BFF-en sem áthaladva — és a jegy soha nem „vár" a
   * feltöltésre (ha egy fájl elhasal, a jegy attól még megnyílt, és a hiba név szerint jelzett).
   */
  const [files, setFiles] = useState<File[]>([]);

  const plainLength = richTextToPlainText(description).length;

  return (
    <FormSheet
      open={open}
      onOpenChange={(o) => {
        if (!o) reset(EMPTY);
        onOpenChange(o);
      }}
      title={t('tickets.createTitle')}
      description={t('tickets.createDesc')}
      onSubmit={onSubmit}
      busy={mutation.isPending}
      footer={
        <>
          <Button
            variant="outline"
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            {t('tickets.cancel')}
          </Button>
          <Button type="submit" variant="brand" disabled={mutation.isPending}>
            {mutation.isPending ? t('tickets.saving') : t('tickets.create')}
          </Button>
        </>
      }
    >
      {/* LAPOS mező-lista — NINCS számozott szakasz (1/2/3) és nincs szekció-fejléc (user-döntés,
          2026-08-25): a rendszer EGYETLEN beúszó panelén sincs ilyen, és a mezők sorrendje magától vezet
          (fiók → szolgáltatás/igénylő → típus/tárgy/leírás → mellékletek → linkek). A számozás azt sugallta,
          hogy lépések vannak, holott egyetlen űrlap. */}
      <Form {...form}>
        <div className="space-y-4">
          <ComboboxField<FormValues>
            name="accountId"
            label={t('tickets.fields.account')}
            required
            search={searchAccounts}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <SelectField<FormValues>
                name="serviceId"
                label={t('tickets.fields.service')}
                required
                disabled={accountId === '' || optionsLoading}
                placeholder={
                  accountId === ''
                    ? t('tickets.form.chooseAccountFirst')
                    : optionsLoading
                      ? t('common.loading')
                      : t('tickets.form.chooseService')
                }
                groups={serviceGroups}
              />
              {/* A jegy ÚTJA — csak jelzés (a szerver dönt): melyik csapathoz kerül a választott szolgáltatással. */}
              {selectedService !== undefined && (
                <p className="text-subtle flex items-center gap-1.5 text-xs">
                  <UsersIcon className="size-3.5 shrink-0" />
                  {selectedService.supportTeamName !== null
                    ? t('tickets.form.routesTo', { team: selectedService.supportTeamName })
                    : t('tickets.form.routesToUnknown')}
                </p>
              )}
            </div>
            <SelectField<FormValues>
              name="requesterId"
              label={t('tickets.fields.requester')}
              required
              disabled={accountId === '' || optionsLoading || requesterOptions.length === 0}
              placeholder={
                accountId === ''
                  ? t('tickets.form.chooseAccountFirst')
                  : optionsLoading
                    ? t('common.loading')
                    : requesterOptions.length === 0
                      ? t('tickets.form.noContacts')
                      : t('tickets.form.chooseRequester')
              }
              options={requesterOptions}
              {...(accountId !== '' && !optionsLoading && requesterOptions.length === 0
                ? { hint: t('tickets.form.noContactsHint') }
                : {})}
            />
          </div>
          {/* TÍPUS: a LISTA-OLDALI badge-ekkel AZONOS megjelenés (user-döntés, 2026-08-25) — ugyanaz a
                szín-kód, ugyanaz az ikon, kisbetűs felirat. A kiválasztott badge teljes erővel áll, a többi
                halványan (`opacity`) — így a választó és a lista ugyanazt a nyelvet beszéli. */}
          <div className="space-y-1.5">
            <Label>
              {t('tickets.fields.type')}
              <span className="text-destructive"> *</span>
            </Label>
            <div
              role="radiogroup"
              aria-label={t('tickets.fields.type')}
              className="flex flex-wrap gap-2"
            >
              {typeOptions.map(({ value, label }) => {
                const active = watch('type') === value;
                return (
                  <button
                    key={value}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    onClick={() => setValue('type', value, { shouldValidate: true })}
                    className={cn(
                      'focus-visible:ring-accent-600 rounded-full transition-opacity focus:outline-none focus-visible:ring-2',
                      active ? 'opacity-100' : 'opacity-45 hover:opacity-80',
                    )}
                  >
                    <TicketTypeBadge type={value} label={label} />
                  </button>
                );
              })}
            </div>
          </div>
          <div className="space-y-1.5">
            <TextField<FormValues>
              name="subject"
              label={t('tickets.fields.subject')}
              required
              placeholder={t('tickets.form.subjectPlaceholder')}
            />
            {/* Élő számláló: a plafon közelében válik hangsúlyossá (nem zajos, amíg messze vagyunk tőle). */}
            <p
              className={cn(
                'text-right text-xs tabular-nums',
                subject.length > SUBJECT_MAX - 25 ? 'text-warning' : 'text-subtle',
              )}
            >
              {subject.length} / {SUBJECT_MAX}
            </p>
          </div>
          <div className="space-y-1.5">
            <RichTextField<FormValues>
              imageAccountId={accountId === '' ? null : accountId}
              name="description"
              label={t('tickets.fields.description')}
              required
              placeholder={t('tickets.form.descriptionPlaceholder')}
              // A min. 25 karakter ÉLŐ visszajelzése (a legacy csak mentéskor szólt) — a látható szöveg hosszán.
              // EZ a mező egyetlen üzenet-sora: mentési kísérletnél ugyanez vált hibára (piros), a Zod-üzenet nem
              // áll alá másodiknak — mint a portál űrlapján.
              status={(invalid) => (
                <p
                  className={cn(
                    'text-xs tabular-nums',
                    plainLength >= DESCRIPTION_MIN
                      ? 'text-subtle'
                      : invalid
                        ? 'text-destructive font-medium'
                        : 'text-warning',
                  )}
                >
                  {plainLength >= DESCRIPTION_MIN
                    ? t('tickets.form.descriptionOk', { count: plainLength })
                    : t('tickets.form.descriptionRemaining', {
                        count: DESCRIPTION_MIN - plainLength,
                      })}
                </p>
              )}
            />
          </div>
          {/* Csatolmányok — a legacy nyitó-formján is volt. A fájlok a jegy létrejötte UTÁN mennek fel
                (a KÖZÖS mező; ugyanezt használja az üzenet-írás). */}
          <PendingAttachmentsField
            files={files}
            onChange={setFiles}
            disabled={mutation.isPending}
            labels={{
              title: t('tickets.attachments.title'),
              add: t('tickets.attachments.add'),
              remove: (name) => t('tickets.attachments.remove', { name }),
            }}
            messages={{
              limit: t('toast:tickets.attachmentLimit', { max: MAX_ATTACHMENTS }),
              invalidType: t('toast:tickets.attachmentInvalidType'),
              tooLarge: t('toast:tickets.attachmentTooLarge'),
            }}
          />

          {/* FÁJL-MEGOSZTÁSI LINKEK — a MELLÉKLETEK-kel EGYENÉRTÉKŰ blokk (ugyanaz a `Label`-tónus), és NEM
                legördülő (user-döntés, 2026-08-25). A három input egymás alatt, mindegyik elején a szolgáltatás
                egyszínű jelével — pontosan úgy, ahogy az üzenet-írás „Linkek" blokkja. A placeholder marad. */}
          <div className="space-y-2">
            <Label className="block">{t('tickets.links.title')}</Label>
            <div className="space-y-2">
              <TextField<FormValues>
                name="googleDriveLink"
                label={t('tickets.links.googleDrive')}
                hideLabel
                leadingIcon={<GoogleDriveMark className="size-4" />}
                placeholder={t('tickets.links.placeholder')}
              />
              <TextField<FormValues>
                name="microsoftOnedriveLink"
                label={t('tickets.links.onedrive')}
                hideLabel
                leadingIcon={<OneDriveMark className="size-4" />}
                placeholder={t('tickets.links.placeholder')}
              />
              <TextField<FormValues>
                name="dropboxLink"
                label={t('tickets.links.dropbox')}
                hideLabel
                leadingIcon={<DropboxMark className="size-4" />}
                placeholder={t('tickets.links.placeholder')}
              />
            </div>
          </div>
        </div>
      </Form>
    </FormSheet>
  );
}
